import { useCallback, useEffect, useMemo, useState } from "react";
import { ApiError, api } from "../api/client";
import type { LessonScheduleScope } from "../api/reports";
import { useAuth } from "../auth/AuthContext";
import { ScheduleExportButton } from "../components/schedule/ScheduleExportButton";
import { CalendarRefHeader } from "../components/calendar/CalendarRefHeader";
import type { CalendarView } from "../components/calendar/calendarTypes";
import { useBranchList } from "../api/branches";
import { useDirectoryBundle } from "../api/directory";
import { useInstructorList } from "../api/instructors";
import { ScheduleSettingsModal } from "../components/calendar/ScheduleSettingsModal";
import { AuditHistoryModal } from "../components/calendar/AuditHistoryModal";
import { CancelAppointmentModal } from "../components/calendar/CancelAppointmentModal";
import { ReactivateAppointmentModal } from "../components/calendar/ReactivateAppointmentModal";
import { DailyWeekStack, type WeekDayGrid } from "../components/calendar/DailyWeekStack";
import { DailyInstructorGrid, type DayGridResponse } from "../components/calendar/DailyInstructorGrid";
import { InstructorPillFilter } from "../components/calendar/InstructorPillFilter";
import {
  NewAppointmentModal,
  NewAppointmentSlotContext,
} from "../components/calendar/NewAppointmentModal";
import { AppointmentDragVehicleModal } from "../components/calendar/AppointmentDragVehicleModal";
import {
  type AppointmentConflictRow,
  type AppointmentDragDropTarget,
  buildIsoDateTime,
  canResolveByChangingVehicle,
  formatConflictMessage,
  normalizeDragLessonType,
} from "../components/calendar/appointmentDrag";
import type { AppointmentEditContext } from "../components/calendar/WeeklyCalendar";
import { MonthlyCalendar, type MonthDayCounts } from "../components/calendar/MonthlyCalendar";
import LessonReportModal from "../components/lesson-reports/LessonReportModal";
import { UI_LOADING } from "../constants/uiLabels";
import {
  addDays,
  formatDailyDateDmY,
  formatRangeLabel,
  parseIsoDate,
  startOfWeekMonday,
  toIsoDate,
} from "../utils/dates";
import "./CalendarPage.css";
import "./CalendarCompact.css";

const MONTH_LABELS_TR = [
  "Ocak",
  "Şubat",
  "Mart",
  "Nisan",
  "Mayıs",
  "Haziran",
  "Temmuz",
  "Ağustos",
  "Eylül",
  "Ekim",
  "Kasım",
  "Aralık",
];

type MonthSummaryResponse = {
  year: number;
  month: number;
  days: Array<{
    date: string;
    total_count: number;
    completed_count: number;
    active_count: number;
  }>;
};

function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1, 0, 0, 0, 0);
}

const WEEKDAY_NAMES_TR = ["Pazar", "Pazartesi", "Salı", "Çarşamba", "Perşembe", "Cuma", "Cumartesi"];

function filterDayGridByInstructors(
  data: DayGridResponse,
  filterAllInstructors: boolean,
  selectedInstructorIds: string[],
): DayGridResponse {
  if (filterAllInstructors || selectedInstructorIds.length === 0) return data;
  const set = new Set(selectedInstructorIds);
  return { ...data, rows: data.rows.filter((r) => set.has(r.instructor_id)) };
}

export function CalendarPage() {
  const [view, setView] = useState<CalendarView>("daily");
  const [dailyAnchor, setDailyAnchor] = useState<Date>(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  });
  const [weekStart, setWeekStart] = useState<Date>(() => startOfWeekMonday(new Date()));
  const [filterAllInstructors, setFilterAllInstructors] = useState(true);
  const [selectedInstructorIds, setSelectedInstructorIds] = useState<string[]>([]);

  const [dayData, setDayData] = useState<DayGridResponse | null>(null);
  const [weekDayGrids, setWeekDayGrids] = useState<Array<{ iso: string; data: DayGridResponse }> | null>(null);
  const [scrollTargetIso, setScrollTargetIso] = useState<string | null>(null);
  const [monthSummary, setMonthSummary] = useState<MonthSummaryResponse | null>(null);
  const [monthAnchor, setMonthAnchor] = useState<Date>(() => startOfMonth(new Date()));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [newAppointment, setNewAppointment] = useState<NewAppointmentSlotContext | null>(null);
  const [editAppointment, setEditAppointment] = useState<AppointmentEditContext | null>(null);
  const [auditAppointmentId, setAuditAppointmentId] = useState<string | null>(null);
  const [lessonReportFor, setLessonReportFor] = useState<{
    id: string;
    starts_at: string;
    ends_at: string;
    lesson_type: string;
    status: string;
    student_id?: string;
    student_name?: string | null;
  } | null>(null);

  const [cancelAppointmentFor, setCancelAppointmentFor] = useState<{
    id: string;
    student_name?: string | null;
    starts_at: string;
    ends_at: string;
  } | null>(null);
  const [reactivateAppointmentFor, setReactivateAppointmentFor] = useState<{
    id: string;
    student_name?: string | null;
    starts_at: string;
    ends_at: string;
  } | null>(null);
  const [scheduleSettingsOpen, setScheduleSettingsOpen] = useState(false);
  const [dragVehiclePrompt, setDragVehiclePrompt] = useState<{
    target: AppointmentDragDropTarget;
    message: string;
  } | null>(null);
  const [dragFeedback, setDragFeedback] = useState<string | null>(null);

  const directory = useDirectoryBundle();

  type DragPlanResponse = {
    created?: boolean;
    requires_override?: boolean;
    conflicts?: AppointmentConflictRow[];
    message?: string;
  };
  type DragMoveResponse = {
    moved?: boolean;
    requires_override?: boolean;
    conflicts?: AppointmentConflictRow[];
    message?: string;
  };

  const executeAppointmentDrag = useCallback(
    async (target: AppointmentDragDropTarget, vehicleId: string): Promise<"ok" | "vehicle" | "blocked"> => {
      const startsAt = buildIsoDateTime(target.date, target.startHHmm);
      const endsAt = buildIsoDateTime(target.date, target.endHHmm);
      const lessonType = normalizeDragLessonType(target.source.lessonType);
      const instructor = directory.data?.instructors.find((i) => i.id === target.instructorId);
      const branchId = instructor?.branch_id;

      if (target.mode === "copy") {
        const payload: Record<string, unknown> = {
          studentId: target.source.studentId,
          instructorId: target.instructorId,
          vehicleId,
          startsAt,
          endsAt,
          lessonType,
        };
        if (branchId) payload.branchId = branchId;
        const res = await api.post<DragPlanResponse>("/appointments/plan", payload);
        if (res.created) return "ok";
        if (res.requires_override) {
          const conflicts = Array.isArray(res.conflicts) ? res.conflicts : [];
          if (canResolveByChangingVehicle(conflicts)) return "vehicle";
          throw new Error(formatConflictMessage(conflicts, res.message));
        }
        throw new Error(res.message || "Kopyalama başarısız.");
      }

      const res = await api.post<DragMoveResponse>(`/appointments/${target.source.appointmentId}/move`, {
        startsAt,
        endsAt,
        instructorId: target.instructorId,
        vehicleId,
      });
      if (res.moved) return "ok";
      if (res.requires_override) {
        const conflicts = Array.isArray(res.conflicts) ? res.conflicts : [];
        if (canResolveByChangingVehicle(conflicts)) return "vehicle";
        throw new Error(formatConflictMessage(conflicts, res.message));
      }
      throw new Error(res.message || "Taşıma başarısız.");
    },
    [directory.data?.instructors],
  );

  const handleAppointmentDragDrop = useCallback(
    async (target: AppointmentDragDropTarget) => {
      setDragFeedback(null);
      setDragVehiclePrompt(null);
      try {
        const result = await executeAppointmentDrag(target, target.source.vehicleId);
        if (result === "ok") {
          setReloadKey((k) => k + 1);
          setDragFeedback(
            target.mode === "copy"
              ? `Kopyalandı: ${target.source.studentName || "Kursiyer"} → ${target.instructorName} ${target.startHHmm}`
              : `Taşındı: ${target.source.studentName || "Kursiyer"} → ${target.instructorName} ${target.startHHmm}`,
          );
          return;
        }
        if (result === "vehicle") {
          setDragVehiclePrompt({
            target,
            message: "Seçili araç bu saatte uygun değil. Başka bir araç seçip tekrar deneyin.",
          });
        }
      } catch (err) {
        const msg =
          err instanceof ApiError
            ? err.message
            : err instanceof Error
              ? err.message
              : "İşlem başarısız.";
        setDragFeedback(msg);
      }
    },
    [executeAppointmentDrag],
  );

  const todayIso = useMemo(() => toIsoDate(new Date()), [reloadKey]);
  const dailyAnchorIso = useMemo(() => toIsoDate(dailyAnchor), [dailyAnchor]);
  const isDailyToday = dailyAnchorIso === todayIso;
  const startIso = useMemo(() => toIsoDate(weekStart), [weekStart]);
  const endIso = useMemo(() => toIsoDate(addDays(weekStart, 6)), [weekStart]);
  const weekDayIsos = useMemo(
    () => Array.from({ length: 7 }, (_, i) => toIsoDate(addDays(weekStart, i))),
    [weekStart],
  );
  const weekDayIsosKey = weekDayIsos.join(",");
  const monthAnchorKey = useMemo(
    () => `${monthAnchor.getFullYear()}-${monthAnchor.getMonth()}`,
    [monthAnchor],
  );
  const countsByDate = useMemo(() => {
    const m = new Map<string, MonthDayCounts>();
    if (!monthSummary?.days) return m;
    for (const row of monthSummary.days) {
      const key = String(row.date).slice(0, 10);
      m.set(key, {
        total_count: row.total_count,
        completed_count: row.completed_count,
        active_count: row.active_count,
      });
    }
    return m;
  }, [monthSummary]);

  const instructorIdsQuery = useMemo(() => {
    if (filterAllInstructors || selectedInstructorIds.length === 0) return undefined;
    return [...selectedInstructorIds].sort().join(",");
  }, [filterAllInstructors, selectedInstructorIds]);

  const instructorList = useInstructorList({
    isActive: true,
    limit: 200,
    offset: 0,
  });

  const displayedTodayData = useMemo(() => {
    if (!dayData) return null;
    return filterDayGridByInstructors(dayData, filterAllInstructors, selectedInstructorIds);
  }, [dayData, filterAllInstructors, selectedInstructorIds]);

  const displayedWeekDays = useMemo((): WeekDayGrid[] | null => {
    if (!weekDayGrids) return null;
    return weekDayGrids.map(({ iso, data }) => {
      const d = parseIsoDate(iso);
      const grid = filterDayGridByInstructors(data, filterAllInstructors, selectedInstructorIds);
      return {
        iso,
        weekday: WEEKDAY_NAMES_TR[d.getDay()] || "",
        dmY: formatDailyDateDmY(iso),
        isToday: iso === todayIso,
        data: grid,
      };
    });
  }, [weekDayGrids, filterAllInstructors, selectedInstructorIds, todayIso]);

  useEffect(() => {
    if (!filterAllInstructors && selectedInstructorIds.length === 0) {
      setFilterAllInstructors(true);
    }
  }, [filterAllInstructors, selectedInstructorIds]);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);

    if (view === "daily") {
      api
        .get<DayGridResponse>("/scheduling/slots-day-grid", { date: dailyAnchorIso }, { signal: controller.signal })
        .then((response) => {
          setDayData(response);
          setWeekDayGrids(null);
          setMonthSummary(null);
        })
        .catch((err) => {
          if (controller.signal.aborted) return;
          if (err instanceof ApiError) {
            setError(err.message);
          } else {
            setError("Takvim verisi Yüklenemedi.");
          }
          setDayData(null);
        })
        .finally(() => {
          if (!controller.signal.aborted) setLoading(false);
        });
    } else if (view === "weekly") {
      Promise.all(
        weekDayIsos.map((iso) =>
          api
            .get<DayGridResponse>("/scheduling/slots-day-grid", { date: iso }, { signal: controller.signal })
            .then((data) => ({ iso, data })),
        ),
      )
        .then((rows) => {
          setWeekDayGrids(rows);
          setDayData(null);
          setMonthSummary(null);
        })
        .catch((err) => {
          if (controller.signal.aborted) return;
          if (err instanceof ApiError) {
            setError(err.message);
          } else {
            setError("Takvim verisi Yüklenemedi.");
          }
          setWeekDayGrids(null);
        })
        .finally(() => {
          if (!controller.signal.aborted) setLoading(false);
        });
    } else {
      const y = monthAnchor.getFullYear();
      const mo = monthAnchor.getMonth() + 1;
      const query: Record<string, string> = {
        year: String(y),
        month: String(mo),
      };
      if (instructorIdsQuery) {
        query.instructor_ids = instructorIdsQuery;
      }
      api
        .get<MonthSummaryResponse>("/scheduling/slots-month", query, { signal: controller.signal })
        .then((response) => {
          setMonthSummary(response);
          setWeekDayGrids(null);
          setDayData(null);
        })
        .catch((err) => {
          if (controller.signal.aborted) return;
          if (err instanceof ApiError) {
            setError(err.message);
          } else {
            setError("Takvim verisi Yüklenemedi.");
          }
          setMonthSummary(null);
        })
        .finally(() => {
          if (!controller.signal.aborted) setLoading(false);
        });
    }

    return () => controller.abort();
  }, [view, dailyAnchorIso, weekDayIsosKey, monthAnchorKey, reloadKey, instructorIdsQuery]);

  useEffect(() => {
    if (view !== "weekly" || !displayedWeekDays || !scrollTargetIso) return;
    const el = document.getElementById(`kp-cal-day-${scrollTargetIso}`);
    el?.scrollIntoView({ behavior: "smooth", block: "start" });
    setScrollTargetIso(null);
  }, [view, displayedWeekDays, scrollTargetIso]);

  const goPrevWeek = () => setWeekStart((d) => addDays(d, -7));
  const goNextWeek = () => setWeekStart((d) => addDays(d, 7));
  const goThisWeek = () => setWeekStart(startOfWeekMonday(new Date()));

  const goPrevMonth = () => setMonthAnchor((d) => new Date(d.getFullYear(), d.getMonth() - 1, 1, 0, 0, 0, 0));
  const goNextMonth = () => setMonthAnchor((d) => new Date(d.getFullYear(), d.getMonth() + 1, 1, 0, 0, 0, 0));
  const goThisMonth = () => setMonthAnchor(startOfMonth(new Date()));

  const goPrevDay = () => setDailyAnchor((d) => addDays(d, -1));
  const goNextDay = () => setDailyAnchor((d) => addDays(d, 1));
  const goToday = () => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    setDailyAnchor(d);
  };

  const handleDailyAnchorChange = (iso: string) => {
    const [y, m, day] = iso.split("-").map(Number);
    if (!y || !m || !day) return;
    setDailyAnchor(new Date(y, m - 1, day, 0, 0, 0, 0));
  };

  const openDayFromMonth = (iso: string) => {
    const [y, m, day] = iso.split("-").map(Number);
    if (!y || !m || !day) return;
    const next = new Date(y, m - 1, day, 0, 0, 0, 0);
    if (iso === todayIso) {
      setDailyAnchor(next);
      setView("daily");
      return;
    }
    setWeekStart(startOfWeekMonday(next));
    setScrollTargetIso(iso);
    setView("weekly");
  };

  const selectAllInstructors = () => {
    setFilterAllInstructors(true);
    setSelectedInstructorIds([]);
  };

  const toggleInstructorCheckbox = (id: string) => {
    if (filterAllInstructors) {
      setFilterAllInstructors(false);
      setSelectedInstructorIds([id]);
      return;
    }
    setSelectedInstructorIds((prev) => {
      if (prev.includes(id)) {
        return prev.filter((x) => x !== id);
      }
      return [...prev, id];
    });
  };

  const instructors = instructorList.data?.items || [];

  const dailyBannerWeekday = WEEKDAY_NAMES_TR[parseIsoDate(dailyAnchorIso).getDay()] || "";
  const dailyBannerDmY = formatDailyDateDmY(dailyAnchorIso);

  const handleWeekAnchorChange = (iso: string) => {
    const [y, m, day] = iso.split("-").map(Number);
    if (!y || !m || !day) return;
    const picked = new Date(y, m - 1, day, 0, 0, 0, 0);
    setWeekStart(startOfWeekMonday(picked));
  };

  const focusDay = (iso: string) => {
    if (iso === todayIso) {
      const [y, m, day] = iso.split("-").map(Number);
      if (y && m && day) setDailyAnchor(new Date(y, m - 1, day, 0, 0, 0, 0));
      setView("daily");
      return;
    }
    document.getElementById(`kp-cal-day-${iso}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const monthTitle = `${MONTH_LABELS_TR[monthAnchor.getMonth()]} ${monthAnchor.getFullYear()}`;

  const { session } = useAuth();
  const canExportSchedule =
    session?.user?.role === "tenant_admin" ||
    session?.user?.role === "manager" ||
    session?.user?.role === "office_staff";
  const canManageSchedule = canExportSchedule;
  const branchList = useBranchList({ isActive: true });
  const scheduleBranchId = session?.user?.branch_id ?? null;
  const scheduleBranchName =
    branchList.data?.find((b) => b.id === scheduleBranchId)?.name ?? null;

  const scheduleExport = useMemo(() => {
    if (!canExportSchedule) return undefined;
    let scope: LessonScheduleScope;
    let start: string;
    let end: string;
    if (view === "daily") {
      scope = "daily";
      start = dailyAnchorIso;
      end = dailyAnchorIso;
    } else if (view === "weekly") {
      scope = "weekly";
      start = startIso;
      end = endIso;
    } else {
      scope = "monthly";
      start = toIsoDate(startOfMonth(monthAnchor));
      end = toIsoDate(new Date(monthAnchor.getFullYear(), monthAnchor.getMonth() + 1, 0));
    }
    const instructorIds =
      !filterAllInstructors && selectedInstructorIds.length > 0 ? selectedInstructorIds : undefined;
    return { scope, startDate: start, endDate: end, instructorIds };
  }, [
    canExportSchedule,
    view,
    dailyAnchorIso,
    startIso,
    endIso,
    monthAnchor,
    filterAllInstructors,
    selectedInstructorIds,
  ]);

  return (
    <div className="kp-calendar-page kp-calendar-page--ref">
      <div className="kp-calendar-page__sticky">
        <CalendarRefHeader
          view={view}
          onViewChange={setView}
          dailyBannerWeekday={dailyBannerWeekday}
          dailyBannerDmY={dailyBannerDmY}
          weekAnchorIso={startIso}
          onWeekAnchorChange={handleWeekAnchorChange}
          weekRangeLabel={formatRangeLabel(startIso, endIso)}
          monthTitle={monthTitle}
          onPrevWeek={goPrevWeek}
          onNextWeek={goNextWeek}
          onThisWeek={goThisWeek}
          onPrevMonth={goPrevMonth}
          onNextMonth={goNextMonth}
          onThisMonth={goThisMonth}
          dailyAnchorIso={dailyAnchorIso}
          onDailyAnchorChange={handleDailyAnchorChange}
          onPrevDay={goPrevDay}
          onNextDay={goNextDay}
          onToday={goToday}
          isDailyToday={isDailyToday}
          onOpenMonthly={() => {
            setMonthAnchor(startOfMonth(weekStart));
            setView("monthly");
          }}
          showScheduleSettings={canManageSchedule}
          onOpenScheduleSettings={() => setScheduleSettingsOpen(true)}
        />

        <InstructorPillFilter
          instructors={instructors}
          filterAllInstructors={filterAllInstructors}
          selectedInstructorIds={selectedInstructorIds}
          loading={instructorList.loading}
          error={instructorList.error}
          onSelectAll={selectAllInstructors}
          onToggleInstructor={toggleInstructorCheckbox}
        />

        <div className="kp-calendar-page__legend kp-calendar-page__legend--ref">
        {scheduleExport && (
          <ScheduleExportButton
            scope={scheduleExport.scope}
            startDate={scheduleExport.startDate}
            endDate={scheduleExport.endDate}
            instructorIds={scheduleExport.instructorIds}
            className="kp-calendar-page__schedule-export"
          />
        )}
        <span className="kp-calendar-page__legend-item kp-calendar-page__legend-item--avail">
          <i className="kp-legend-sq" aria-hidden /> + Rezervasyon Yap
        </span>
        <span className="kp-calendar-page__legend-item">
          <i className="kp-legend-sq kp-legend-sq--lesson-normal" aria-hidden /> Normal Ders · Dolu
        </span>
        <span className="kp-calendar-page__legend-item">
          <i className="kp-legend-sq kp-legend-sq--lesson-extra" aria-hidden /> Ek Ders · Dolu
        </span>
        <span className="kp-calendar-page__legend-item">
          <i className="kp-legend-sq kp-legend-sq--lesson-special" aria-hidden /> Özel Ders · Dolu
        </span>
        <span className="kp-calendar-page__legend-item kp-calendar-page__legend-item--done">
          <i className="kp-legend-sq kp-legend-sq--check" aria-hidden /> Tamamlandı
        </span>
        <span className="kp-calendar-page__legend-item kp-calendar-page__legend-item--eval">
          <i className="kp-legend-sq kp-legend-sq--warn" aria-hidden /> Değerlendirme Bekliyor
        </span>
        <span className="kp-calendar-page__legend-item kp-calendar-page__legend-item--leave">
          <i className="kp-legend-sq" aria-hidden /> Eğitmen İzinli
        </span>
        <span className="kp-calendar-page__legend-item kp-calendar-page__legend-item--noshow">
          <i className="kp-legend-sq kp-legend-sq--x" aria-hidden /> Kursiyer Gelmedi
        </span>
        <span className="kp-calendar-page__legend-item kp-calendar-page__legend-item--cancelled">
          <i className="kp-legend-sq kp-legend-sq--cancelled" aria-hidden /> Ders İptal Edildi
        </span>
        {view === "weekly" && (
          <span className="kp-calendar-page__hint">Haftalık görünümde günler aşağı doğru listelenir.</span>
        )}
        {view === "monthly" && (
          <span className="kp-calendar-page__hint">
            Aylık görünümde yeşil tonlar randevu yoğunluğunu gösterir; bir güne tıklayın.
          </span>
        )}
        </div>
      </div>

      <div className="kp-calendar-page__scroll">
        {loading && <div className="kp-card kp-loading">Takvim {UI_LOADING}</div>}
        {error && <div className="kp-card kp-error">{error}</div>}
        {dragFeedback && (
          <div className="kp-card kp-calendar-page__drag-feedback" role="status">
            {dragFeedback}
            <button type="button" className="kp-btn" onClick={() => setDragFeedback(null)}>
              Kapat
            </button>
          </div>
        )}
        {!loading && !error && view === "daily" && displayedTodayData && (
          <DailyInstructorGrid
            data={displayedTodayData}
            onReserve={(ctx) => setNewAppointment(ctx)}
            onOpenLessonReport={(appt) => setLessonReportFor(appt)}
            onShowAuditHistory={(id) => setAuditAppointmentId(id)}
            onEditReservation={(ctx) => setEditAppointment(ctx)}
            onCancelAppointment={(appt) =>
              setCancelAppointmentFor({
                id: appt.id,
                student_name: appt.student_name,
                starts_at: appt.starts_at,
                ends_at: appt.ends_at,
              })
            }
            onReactivateAppointment={(appt) =>
              setReactivateAppointmentFor({
                id: appt.id,
                student_name: appt.student_name,
                starts_at: appt.starts_at,
                ends_at: appt.ends_at,
              })
            }
            onAppointmentDragDrop={(t) => void handleAppointmentDragDrop(t)}
          />
        )}
        {!loading && !error && view === "weekly" && displayedWeekDays && displayedWeekDays.length > 0 && (
          <DailyWeekStack
            days={displayedWeekDays}
            onReserve={(ctx) => setNewAppointment(ctx)}
            onOpenLessonReport={(appt) => setLessonReportFor(appt)}
            onShowAuditHistory={(id) => setAuditAppointmentId(id)}
            onEditReservation={(ctx) => setEditAppointment(ctx)}
            onCancelAppointment={(appt) =>
              setCancelAppointmentFor({
                id: appt.id,
                student_name: appt.student_name,
                starts_at: appt.starts_at,
                ends_at: appt.ends_at,
              })
            }
            onReactivateAppointment={(appt) =>
              setReactivateAppointmentFor({
                id: appt.id,
                student_name: appt.student_name,
                starts_at: appt.starts_at,
                ends_at: appt.ends_at,
              })
            }
            onAppointmentDragDrop={(t) => void handleAppointmentDragDrop(t)}
            onScrollToDay={focusDay}
          />
        )}
        {!loading && !error && view === "monthly" && monthSummary && (
          <MonthlyCalendar
            year={monthSummary.year}
            month={monthSummary.month}
            countsByDate={countsByDate}
            onSelectDay={openDayFromMonth}
          />
        )}
      </div>

      {dragVehiclePrompt && (
        <AppointmentDragVehicleModal
          target={dragVehiclePrompt.target}
          message={dragVehiclePrompt.message}
          onClose={() => setDragVehiclePrompt(null)}
          onConfirm={async (vehicleId) => {
            const target = dragVehiclePrompt.target;
            const result = await executeAppointmentDrag(target, vehicleId);
            if (result === "ok") {
              setDragVehiclePrompt(null);
              setReloadKey((k) => k + 1);
              setDragFeedback(
                target.mode === "copy"
                  ? `Kopyalandı: ${target.source.studentName || "Kursiyer"} → ${target.instructorName} ${target.startHHmm}`
                  : `Taşındı: ${target.source.studentName || "Kursiyer"} → ${target.instructorName} ${target.startHHmm}`,
              );
              return;
            }
            if (result === "vehicle") {
              throw new Error("Seçilen araç da uygun değil. Başka araç deneyin.");
            }
            throw new Error("İşlem tamamlanamadı.");
          }}
        />
      )}

      {(newAppointment || editAppointment) && (
        <NewAppointmentModal
          slot={
            editAppointment
              ? {
                  date: editAppointment.date,
                  startHHmm: editAppointment.startHHmm,
                  endHHmm: editAppointment.endHHmm,
                  preferredInstructorId: editAppointment.preferredInstructorId,
                }
              : newAppointment!
          }
          edit={editAppointment ?? undefined}
          onClose={() => {
            setNewAppointment(null);
            setEditAppointment(null);
          }}
          onCreated={() => {
            setNewAppointment(null);
            setEditAppointment(null);
            setReloadKey((k) => k + 1);
          }}
        />
      )}

      {auditAppointmentId && (
        <AuditHistoryModal appointmentId={auditAppointmentId} onClose={() => setAuditAppointmentId(null)} />
      )}

      {lessonReportFor && (
        <LessonReportModal
          appointmentId={lessonReportFor.id}
          studentId={lessonReportFor.student_id}
          studentName={lessonReportFor.student_name ?? undefined}
          startsAt={lessonReportFor.starts_at}
          endsAt={lessonReportFor.ends_at}
          onClose={() => setLessonReportFor(null)}
          onSaved={() => {
            setReloadKey((k) => k + 1);
          }}
        />
      )}

      {cancelAppointmentFor && (
        <CancelAppointmentModal
          appointmentId={cancelAppointmentFor.id}
          studentName={cancelAppointmentFor.student_name || undefined}
          startsAt={cancelAppointmentFor.starts_at}
          endsAt={cancelAppointmentFor.ends_at}
          onClose={() => setCancelAppointmentFor(null)}
          onCancelled={() => setReloadKey((k) => k + 1)}
          onDeleted={() => setReloadKey((k) => k + 1)}
        />
      )}

      {reactivateAppointmentFor && (
        <ReactivateAppointmentModal
          appointmentId={reactivateAppointmentFor.id}
          studentName={reactivateAppointmentFor.student_name || undefined}
          startsAt={reactivateAppointmentFor.starts_at}
          endsAt={reactivateAppointmentFor.ends_at}
          onClose={() => setReactivateAppointmentFor(null)}
          onReactivated={() => setReloadKey((k) => k + 1)}
        />
      )}

      {canManageSchedule && (
        <ScheduleSettingsModal
          open={scheduleSettingsOpen}
          homeBranchId={scheduleBranchId}
          homeBranchName={scheduleBranchName}
          todayIso={todayIso}
          onClose={() => setScheduleSettingsOpen(false)}
          onSaved={() => setReloadKey((k) => k + 1)}
        />
      )}
    </div>
  );
}
